// services/retailersListSlice.js
import { createEntityAdapter, createSelector } from "@reduxjs/toolkit";
import { apiSlice } from "../apiSlice";
import { retailersListUrl } from "../../config/config";

// ---------------- Adapter ----------------
const retailersAdapter = createEntityAdapter({
  selectId: (retailer) => retailer.id,
});

const initialState = retailersAdapter.getInitialState();

// ---------------- Helpers ----------------
const extractReadableLabel = (url) => {
  if (!url) return "";
  const parts = url.split("/");
  const fileName = parts[parts.length - 1];
  const ext = fileName.split(".").pop();
  return `${fileName.slice(0, 10)}...${ext}`;
};

// ---------------- API ----------------
export const retailersListSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getRetailersList: builder.query({
      query: (filters) => {
        const params = [];
        if (filters.status) params.push(`status=${filters.status}`);
        if (filters.search)
          params.push(`search=${encodeURIComponent(filters.search)}`);

        return params.length
          ? `${retailersListUrl}?${params.join("&")}`
          : retailersListUrl;
      },

      transformResponse: (response) => {
        const normalized = (response.data ?? []).map((item) => ({
          ...item,
          panUrlLabel: extractReadableLabel(item.pan_image_url),
          aadharUrlLabel: extractReadableLabel(item.aadhar_image_url),
        }));

        return retailersAdapter.setAll(initialState, normalized);
      },

      providesTags: (result) => [
        { type: "Retailers", id: "LIST" },
        ...(result?.ids ?? []).map((id) => ({
          type: "Retailers",
          id,
        })),
      ],
    }),

    getRetailerById: builder.query({
      query: (id) => `${retailersListUrl}${id}`,

      transformResponse: (response) => {
        const item = response.data;
        return item
          ? {
              ...item,
              panUrlLabel: extractReadableLabel(item.pan_image_url),
              aadharUrlLabel: extractReadableLabel(item.aadhar_image_url),
            }
          : null;
      },

      providesTags: (result, error, id) => [{ type: "Retailers", id }],
    }),
  }),
});

// ---------------- Hooks ----------------
export const { useGetRetailersListQuery, useGetRetailerByIdQuery } =
  retailersListSlice;

// ---------------- Filter → Query Arg ----------------
const selectRetailerFilter = (state) => state.retailerFilter;

export const selectRetailersQueryArg = createSelector(
  [selectRetailerFilter],
  (filter) => ({
    status: filter.status,
    search: filter.search,
  })
);

// ---------------- Adapter Selectors ----------------
const selectRetailersQueryResult = createSelector(
  [selectRetailersQueryArg, (state) => state],
  (filters, state) =>
    retailersListSlice.endpoints.getRetailersList.select(filters)(state)
);

const selectRetailersData = createSelector(
  [selectRetailersQueryResult],
  (queryResult) => queryResult?.data ?? initialState
);

export const {
  selectAll: selectRetailers,
  selectById: selectRetailerById,
  selectIds: selectRetailerIds,
} = retailersAdapter.getSelectors((state) => selectRetailersData(state));
